import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getTeamLogo } from '../utils/teamUtils';
import { calculatePlayerPerformanceScore } from '../utils/playerUtils';

const BoxScoreGenerator = ({ players, isDarkMode }) => {
  const [selectedRound, setSelectedRound] = useState('');
  const [selectedSeason, setSelectedSeason] = useState('2024');
  const [selectedMatchup, setSelectedMatchup] = useState('');
  const [boxScoreData, setBoxScoreData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [matchups, setMatchups] = useState([]);
  const [seasons, setSeasons] = useState(['2025', '2024', '2023', '2022', '2021',]);
  const [sortColumn, setSortColumn] = useState('player_name');
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc' - default alphabetical
  
  // Extract unique teams, rounds, seasons from data
  useEffect(() => {
    if (players && players.length > 0) {
      // Extract unique teams
      const uniqueTeams = [...new Set(players.map(player => player.team))].filter(Boolean).sort();
      setTeams(uniqueTeams);
      
      // Extract unique rounds and clean up the format
      const uniqueRounds = [...new Set(players.map(player => player.round))]
        .filter(Boolean)
        .sort((a, b) => {
          // Convert round strings to numbers for comparison
          const aValue = a.toLowerCase();
          const bValue = b.toLowerCase();
          
          
          // Extract numbers from round strings and compare
          const aNum = parseInt(a.replace(/\D/g, ''));
          const bNum = parseInt(b.replace(/\D/g, ''));
          return aNum - bNum;
        });

      setRounds(uniqueRounds);
      
      // Set a random default round if none selected
      if (!selectedRound && uniqueRounds.length > 0) {
        const randomRound = uniqueRounds[Math.floor(Math.random() * uniqueRounds.length)];
        setSelectedRound(randomRound);
      }
    }
  }, [players, selectedRound]);
  
  // Update the matchup generation useEffect
  useEffect(() => {
    if (selectedRound && selectedSeason) {
      const seasonStr = selectedSeason.toString();
      
      console.log('Filtering games with:', {
        round: selectedRound,
        season: seasonStr
      });

      // Filter games by round and season
      const filteredGames = players.filter(player => {
        const playerSeason = player.year?.toString();
        return playerSeason === seasonStr && 
               player.round === selectedRound;  // No need to clean round format
      });

      console.log('Filtered games:', filteredGames.length);

      // Create unique matchups using home_team and away_team
      const matchupMap = new Map();
      filteredGames.forEach(game => {
        if (!game.home_team || !game.away_team) return;
        
        // Create a consistent key for the matchup
        const teams = [game.home_team, game.away_team].sort();
        const matchupKey = `${teams[0]} vs ${teams[1]}`;
        
        if (!matchupMap.has(matchupKey)) {
          matchupMap.set(matchupKey, {
            team1: teams[0],
            team2: teams[1],
            players: []
          });
        }
        
        matchupMap.get(matchupKey).players.push(game);
      });

      // Convert map to array of matchup strings
      const uniqueMatchups = Array.from(matchupMap.keys());
      console.log('Generated matchups:', uniqueMatchups);

      setMatchups(uniqueMatchups);
      
      // Set a random default matchup if none selected and matchups are available
      if (!selectedMatchup && uniqueMatchups.length > 0) {
        const randomMatchup = uniqueMatchups[Math.floor(Math.random() * uniqueMatchups.length)];
        setSelectedMatchup(randomMatchup);
      }
    } else {
      setMatchups([]);
      setSelectedMatchup('');
    }
  }, [selectedRound, selectedSeason, players]);
  
  // Update the seasons state to ensure we're using the correct format
  useEffect(() => {
    if (players && players.length > 0) {
      // Extract unique seasons from the data
      const uniqueSeasons = [...new Set(players.map(player => 
        player.season?.toString()
      ))]
        .filter(Boolean)
        .sort((a, b) => b - a); // Sort in descending order
      
      if (uniqueSeasons.length > 0) {
        setSeasons(uniqueSeasons);
        setSelectedSeason(uniqueSeasons[0]); // Set the most recent season as default
      }
    }
  }, [players]);
  
  // Add this useEffect to handle matchup changes
  useEffect(() => {
    if (selectedMatchup) {
      generateBoxScore();
    }
  }, [selectedMatchup]);
  
  const generateBoxScore = () => {
    setLoading(true);
    
    try {
      if (!selectedMatchup) {
        console.log('No matchup selected');
        setBoxScoreData([]);
        return;
      }

      const [team1, team2] = selectedMatchup.split(' vs ');
      console.log('Looking for matchup between:', { team1, team2 });

      // Filter players for the selected matchup
      const filteredPlayers = players.filter(player => {
        const playerSeason = player.year?.toString();
        const matchesSeasonAndRound = playerSeason === selectedSeason.toString() &&
                                     player.round === selectedRound;
        
        // Match players from either team in the matchup
        const isInMatchup = (
          // Match exact home/away team combination
          (player.home_team === team1 && player.away_team === team2) ||
          (player.home_team === team2 && player.away_team === team1) ||
          // Match player's team to either team in the matchup
          (player.player_team === team1 || player.player_team === team2)
        );

        return matchesSeasonAndRound && isInMatchup;
      });

      console.log('Filtered players count:', filteredPlayers.length);
      if (filteredPlayers.length === 0) {
        console.log('No players found matching criteria');
      } else {
        console.log('First matching player:', filteredPlayers[0]);
      }

      // Sort players by team and position
      const sortedPlayers = filteredPlayers.sort((a, b) => {
        // Sort by player_team first
        if (a.player_team !== b.player_team) {
          return a.player_team.localeCompare(b.player_team);
        }
        // Then by position
        if (a.position !== b.position) {
          return a.position.localeCompare(b.position);
        }
        // Finally by player name
        return a.player_name.localeCompare(b.player_name);
      });
      
      console.log('Final sorted players count:', sortedPlayers.length);
      setBoxScoreData(sortedPlayers);
    } catch (error) {
      console.error("Error generating box score:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate team totals
  const calculateTeamTotals = () => {
    if (!boxScoreData.length) return null;
    
    return {
      points: boxScoreData.reduce((sum, player) => sum + (parseInt(player.points) || 0), 0),
      tries: boxScoreData.reduce((sum, player) => sum + (parseInt(player.tries) || 0), 0),
      run_meters: boxScoreData.reduce((sum, player) => sum + (parseInt(player.all_run_metres) || 0), 0),
      tackles: boxScoreData.reduce((sum, player) => sum + (parseInt(player.tackles_made) || 0), 0),
      tackle_busts: boxScoreData.reduce((sum, player) => sum + (parseInt(player.tackle_breaks) || 0), 0),
      line_breaks: boxScoreData.reduce((sum, player) => sum + (parseInt(player.line_breaks) || 0), 0),
      offloads: boxScoreData.reduce((sum, player) => sum + (parseInt(player.offloads) || 0), 0),
      errors: boxScoreData.reduce((sum, player) => sum + (parseInt(player.errors) || 0), 0)
    };
  };
  
  // Find opponent team and score
  const getMatchInfo = () => {
    if (!boxScoreData.length) return null;
    
    // Get opponent from the first player's data (assuming all players have same opponent)
    const opponent = boxScoreData[0].opponent;
    
    // Find opponent's score if available (this might need adjustment based on data structure)
    const opponentScore = 0; // Placeholder - would need to be calculated from data
    const teamScore = boxScoreData.reduce((sum, player) => sum + (parseInt(player.points) || 0), 0);
    
    return { opponent, opponentScore, teamScore };
  };
  
  // First, let's group players by team
  const groupPlayersByTeam = (players) => {
    const teams = [...new Set(players.map(p => p.player_team))];
    return teams.map(team => {
      const teamPlayers = players.filter(p => p.player_team === team);
      
      // Sort players based on current sort criteria
      const sortedPlayers = [...teamPlayers].sort((a, b) => {
        let valueA, valueB;
        
        if (sortColumn === 'player_name') {
          valueA = a.player_name || '';
          valueB = b.player_name || '';
          return sortDirection === 'asc' 
            ? valueA.localeCompare(valueB) 
            : valueB.localeCompare(valueA);
        } else if (sortColumn === 'position') {
          valueA = a.position || '';
          valueB = b.position || '';
          return sortDirection === 'asc' 
            ? valueA.localeCompare(valueB) 
            : valueB.localeCompare(valueA);
        } else if (sortColumn === 'number') {
          valueA = parseInt(a.number) || 0;
          valueB = parseInt(b.number) || 0;
        } else if (sortColumn === 'PP') {
          valueA = calculatePlayerPerformanceScore(a);
          valueB = calculatePlayerPerformanceScore(b);
        } else {
          valueA = parseFloat(a[sortColumn]) || 0;
          valueB = parseFloat(b[sortColumn]) || 0;
        }
        
        return sortDirection === 'asc' 
          ? valueA - valueB 
          : valueB - valueA;
      });
      
      return {
        teamName: team,
        players: sortedPlayers
      };
    });
  };
  
  // Add this sorting handler function
  const handleSort = (column) => {
    // If clicking the same column, toggle direction
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new column, set it and default to appropriate direction
      setSortColumn(column);
      
      // Default to descending for numeric stats, ascending for names
      const isNumeric = ['points', 'tries', 'all_run_metres', 'tackles_made', 'mins_played'].includes(column);
      setSortDirection(isNumeric ? 'desc' : 'asc');
    }
  };
  
  return (
    <div className="h-full w-full px-2">
      <h1 className="text-lg font-bold mb-3 text-gray-800 dark:text-white">Box Score Generator</h1>
      
      {/* Selection Controls - Horizontal layout for desktop */}
      <div className="flex flex-col md:flex-row md:items-end gap-2 md:gap-3 mb-4">
        <div className="md:w-1/3">
          <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Season
          </label>
          <select
            value={selectedSeason}
            onChange={(e) => setSelectedSeason(e.target.value)}
            className="w-full p-1.5 md:p-2 text-xs md:text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            {seasons.map(season => (
              <option key={season} value={season}>{season}</option>
            ))}
          </select>
        </div>
        
        <div className="md:w-1/3">
          <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Round
          </label>
          <select
            value={selectedRound}
            onChange={(e) => setSelectedRound(e.target.value)}
            className="w-full p-1.5 md:p-2 text-xs md:text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">Select Round</option>
            {rounds.map(round => (
              <option key={round} value={round}>
                {round.toLowerCase().includes('round') ? round : 
                  round.toLowerCase().includes('finals') ? round : 
                  `${round}`}
              </option>
            ))}
          </select>
        </div>

        <div className="md:w-1/3">
          <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Matchup
          </label>
          <select
            value={selectedMatchup}
            onChange={(e) => setSelectedMatchup(e.target.value)}
            className={`w-full p-1.5 md:p-2 text-xs md:text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
              !selectedRound || !selectedSeason ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={!selectedRound || !selectedSeason}
          >
            <option value="">
              {!selectedRound || !selectedSeason 
                ? 'Select round and season first'
                : matchups.length === 0 
                  ? 'No matchups found'
                  : 'Select Matchup'}
            </option>
            {matchups.map(matchup => (
              <option key={matchup} value={matchup}>{matchup}</option>
            ))}
          </select>
          {selectedRound && selectedSeason && matchups.length === 0 && (
            <p className="mt-1 text-xs md:text-sm text-red-500">
              No matchups found for the selected criteria
            </p>
          )}
        </div>
      </div>
      
      {/* Box Score Display */}
      {loading ? (
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent dark:border-accent-light"></div>
        </div>
      ) : boxScoreData.length > 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">          
          {/* Teams Container - Side by side on desktop */}
          <div className="flex flex-col md:flex-row md:divide-x md:divide-gray-200 md:dark:divide-gray-700">
            {groupPlayersByTeam(boxScoreData).map((team, teamIndex) => (
              <div key={team.teamName} className={`${teamIndex > 0 ? 'mt-4 md:mt-0' : ''} md:flex-1`}>
                {/* Team Header with Logo */}
                <div className="flex justify-between items-center p-2 md:p-3 bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <img 
                      src={getTeamLogo(team.teamName)} 
                      className="h-6 md:h-8 w-6 md:w-8 object-contain"
                      alt={team.teamName}
                      onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/24?text=NRL'; }}
                    />
                    <div className="text-sm md:text-base font-bold text-gray-900 dark:text-white">
                      {team.teamName}
                    </div>
                  </div>
                  <div className="text-sm md:text-base font-semibold text-gray-900 dark:text-white">
                    {team.players.reduce((sum, player) => sum + (parseInt(player.points) || 0), 0)}
                  </div>
                </div>
                
                {/* Player Stats Table - Improved column spacing */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-20">
                      <tr>
                        <th 
                          className="w-10 px-1 py-1.5 text-center text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleSort('number')}
                        >
                          <div className="flex items-center justify-center">
                            #
                            {sortColumn === 'number' && (
                              <span className="ml-1">
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="w-28 sm:w-32 px-1 py-1.5 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleSort('player_name')}
                        >
                          <div className="flex items-center">
                            Player
                            {sortColumn === 'player_name' && (
                              <span className="ml-1">
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="w-16 px-1 py-1.5 text-center text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleSort('mins_played')}
                        >
                          <div className="flex items-center justify-center">
                            MIN
                            {sortColumn === 'mins_played' && (
                              <span className="ml-1">
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="w-12 px-1 py-1.5 text-center text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleSort('points')}
                        >
                          <div className="flex items-center justify-center">
                            PTS
                            {sortColumn === 'points' && (
                              <span className="ml-1">
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="w-12 px-1 py-1.5 text-center text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleSort('PP')}
                        >
                          <div className="flex items-center justify-center">
                            PP
                            {sortColumn === 'PP' && (
                              <span className="ml-1">
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="w-12 px-1 py-1.5 text-center text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleSort('tries')}
                        >
                          <div className="flex items-center justify-center">
                            TRY
                            {sortColumn === 'tries' && (
                              <span className="ml-1">
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="w-14 px-1 py-1.5 text-center text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleSort('all_run_metres')}
                        >
                          <div className="flex items-center justify-center">
                            RUN
                            {sortColumn === 'all_run_metres' && (
                              <span className="ml-1">
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="w-14 px-1 py-1.5 text-center text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleSort('tackles_made')}
                        >
                          <div className="flex items-center justify-center">
                            TKL
                            {sortColumn === 'tackles_made' && (
                              <span className="ml-1">
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {team.players.map((player, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}>
                          <td className="px-1 py-1.5 text-center text-[10px] sm:text-sm text-gray-500 dark:text-gray-300">
                            {player.number || '-'}
                          </td>
                          <td className="px-1 py-1.5 text-left text-[10px] sm:text-sm text-gray-900 dark:text-gray-100">
                            {player.position ? player.player_name : player.player_name}
                          </td>
                          <td className="px-1 py-1.5 text-center text-[10px] sm:text-sm text-gray-500 dark:text-gray-300">
                            {player.mins_played}
                          </td>
                          <td className="px-1 py-1.5 text-center text-[10px] sm:text-sm text-gray-500 dark:text-gray-300">
                            {player.points || 0}
                          </td>
                          <td className="px-1 py-1.5 text-center text-[10px] sm:text-sm text-gray-500 dark:text-gray-300">
                            {calculatePlayerPerformanceScore(player) || 0}
                          </td>
                          <td className="px-1 py-1.5 text-center text-[10px] sm:text-sm text-gray-500 dark:text-gray-300">
                            {player.tries || 0}
                          </td>
                          <td className="px-1 py-1.5 text-center text-[10px] sm:text-sm text-gray-500 dark:text-gray-300">
                            {player.all_run_metres || 0}
                          </td>
                          <td className="px-1 py-1.5 text-center text-[10px] sm:text-sm text-gray-500 dark:text-gray-300">
                            {player.tackles_made || 0}
                          </td>
                        </tr>
                      ))}
                      
                      {/* Team Totals Row */}
                      <tr className="bg-gray-100 dark:bg-gray-600 font-semibold">
                        <td className="px-1 py-1.5 text-center text-[10px] sm:text-sm text-gray-500 dark:text-gray-300">-</td>
                        <td className="px-1 py-1.5 text-left text-[10px] sm:text-sm font-bold text-gray-900 dark:text-white">
                          TEAM TOTALS
                        </td>
                        <td className="px-1 py-1.5 text-center text-[10px] sm:text-sm text-gray-500 dark:text-gray-300">-</td>
                        <td className="px-1 py-1.5 text-center text-[10px] sm:text-sm text-gray-900 dark:text-white">
                          {team.players.reduce((sum, player) => sum + (parseInt(player.points) || 0), 0)}
                        </td>
                        <td className="px-1 py-1.5 text-center text-[10px] sm:text-sm text-gray-900 dark:text-white">
                          {team.players.reduce((sum, player) => sum + (calculatePlayerPerformanceScore(player) || 0), 0)}
                        </td>
                        <td className="px-1 py-1.5 text-center text-[10px] sm:text-sm text-gray-900 dark:text-white">
                          {team.players.reduce((sum, player) => sum + (parseInt(player.tries) || 0), 0)}
                        </td>
                        <td className="px-1 py-1.5 text-center text-[10px] sm:text-sm text-gray-900 dark:text-white">
                          {team.players.reduce((sum, player) => sum + (parseInt(player.all_run_metres) || 0), 0)}
                        </td>
                        <td className="px-1 py-1.5 text-center text-[10px] sm:text-sm text-gray-900 dark:text-white">
                          {team.players.reduce((sum, player) => sum + (parseInt(player.tackles_made) || 0), 0)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
          
          {/* Team Comparison (Optional feature) */}
          {groupPlayersByTeam(boxScoreData).length > 1 && (
            <div className="mt-4 p-3 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Game Statistics</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.entries(calculateTeamTotals()).map(([stat, value]) => (
                  <div key={stat} className="p-2 bg-gray-50 dark:bg-gray-750 rounded">
                    <div className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400 uppercase">{stat.replace('_', ' ')}</div>
                    <div className="text-sm md:text-base font-medium text-gray-900 dark:text-white">{value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Export Actions */}
          <div className="flex flex-wrap justify-end gap-2 mt-4 px-2 pb-2">
            <button 
              className="px-3 py-1 md:px-4 md:py-2 text-xs md:text-sm bg-accent text-white dark:bg-accent-light dark:text-gray-900 rounded hover:bg-accent/80 dark:hover:bg-accent-light/80 transition-colors"
              onClick={() => window.print()}
            >
              Print
            </button>
            <button 
              className="px-3 py-1 md:px-4 md:py-2 text-xs md:text-sm bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-100 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
              onClick={() => {
                // Simple CSV export - in a production app, use a proper CSV library
                const headers = ["Player", "Position", "Minutes", "Points", "Tries", "Run Meters", "Tackles"];
                const csvContent = [
                  headers.join(','),
                  ...boxScoreData.map(player => [
                    `"${player.player_name}"`,
                    player.position,
                    player.mins_played,
                    player.points || 0,
                    player.tries || 0,
                    player.all_run_metres || 0,
                    player.tackles_made || 0
                  ].join(','))
                ].join('\n');
                
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `boxscore_Round${selectedRound}_${selectedSeason}.csv`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              Export CSV
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 text-center">
          <p className="text-xs md:text-sm text-gray-600 dark:text-gray-300">
            {selectedRound && selectedSeason 
              ? "No box score data available for the selected criteria" 
              : "Select a round and season to generate a box score"}
          </p>
        </div>
      )}
    </div>
  );
};

export default BoxScoreGenerator; 